// Shared preview-overlay chrome that lets the operator click WHOLE regions
// of the rendered site (footer, coming-soon splash, promo hero, etc.) and
// jump straight to the corresponding admin editor accordion. Only renders
// its chrome when inside the admin preview iframe (`useInAdminPreview`);
// public visitors + third-party embeds see the children unwrapped.
//
// Kept in its own file (rather than as an internal helper in
// site-pages.tsx) so any surface that lives outside the SitePagesSite
// component tree (coming-soon splash, promo site, etc.) can import + use
// the same overlay without pulling the whole sitePages renderer.
//
// The BLOCK-level equivalent (per-SiteBlock chrome with reorder/delete/edit)
// lives in site-pages.tsx as BlockAdminOverlay: that one needs access to
// the sitePages block model, this one is entirely generic. Toka 2026-09-20.

import { createContext, useContext, useState, type ReactNode, type MouseEvent, type PointerEvent } from "react";
import {
  useInAdminPreview,
  PREVIEW_SELECT_SECTION_MSG_TYPE,
  type PreviewSelectSectionMessage,
} from "../lib/preview-overlay";

// Context that BlockAdminOverlay provides to its subtree so any renderer
// (gallery, agenda, tiles, etc.) can find its own block index without
// prop-drilling. Used by richer in-preview interactions (gallery image
// drag-reorder → postMessage(PREVIEW_GALLERY_REORDER, { blockIndex, ...})).
// Null outside an overlay — renderers gate on it before enabling admin-only
// interactions.
export const BlockIndexContext = createContext<number | null>(null);
export function useBlockIndex(): number | null {
  return useContext(BlockIndexContext);
}

export function SectionAdminOverlay({
  sectionId,
  label,
  children,
}: {
  /** Matches the `id` on the target `<CollapsibleSection>` in AdminWebsitePage. */
  sectionId: string;
  /** Short human label for the hover chip, e.g. "site footer". */
  label: string;
  children: ReactNode;
}) {
  const inAdmin = useInAdminPreview();
  const [hover, setHover] = useState(false);

  if (!inAdmin) return <>{children}</>;

  const onClick = (e: MouseEvent) => {
    // Stop the click bubbling to any parent block/section overlays that
    // would otherwise fire their own edit action.
    e.stopPropagation();
    if (typeof window === "undefined" || window.self === window.top) return;
    const msg: PreviewSelectSectionMessage = {
      type: PREVIEW_SELECT_SECTION_MSG_TYPE,
      sectionId,
    };
    window.parent.postMessage(msg, window.location.origin);
  };

  return (
    <div
      onPointerEnter={(_e: PointerEvent) => setHover(true)}
      onPointerLeave={(_e: PointerEvent) => setHover(false)}
      onClick={onClick}
      className="relative"
      style={{
        outline: hover ? "3px dashed var(--accent, #3f7a6a)" : "1px solid transparent",
        outlineOffset: hover ? "-3px" : "0",
        cursor: hover ? "pointer" : undefined,
        transition: "outline-color 120ms ease",
      }}
      title={hover ? `Click to edit ${label} in the admin` : undefined}
    >
      {children}
      {hover && (
        <span
          className="pointer-events-none absolute z-40 rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider shadow-xl"
          style={{
            top: "12px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "var(--accent, #3f7a6a)",
            color: "var(--accent-fg, white)",
            letterSpacing: "0.08em",
          }}
        >
          ✎ Edit {label} →
        </span>
      )}
    </div>
  );
}
